import { useState, useRef, useEffect } from "react";
import { AMBASSADOR_VISIBLE } from "@/lib/challenge";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import { User, LogOut, Shield, Mail, Loader2, Heart, BookOpen, Camera, Eye, EyeOff, Crown, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api, avatarUrl, formatApiError, capitalizeFirst } from "@/lib/appresse";
import { ProfileForm } from "@/components/ProfileForm";
import { PostalCity } from "@/components/PostalCity";
import { Wordmark } from "@/components/Wordmark";
import { AccountInfo } from "@/components/AccountInfo";

export default function Compte() {
  const { user, login, register, logout, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(params.get("tab") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loc, setLoc] = useState({ countryCode: "FR", country: "France", postal: "", city: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [monetOn, setMonetOn] = useState(false);
  const [planBadge, setPlanBadge] = useState(null);
  useEffect(() => { api.monetizationPublic().then((d) => setMonetOn(!!d.enabled)).catch(() => {}); }, []);
  useEffect(() => {
    if (!user?.id) return;
    api.mySubscription().then((d) => {
      const s = d?.subscription, m = d?.membership;
      if (s?.status === "ACTIF") setPlanBadge(s.plan_label);
      else if (m?.status === "ACTIF") setPlanBadge(`${m.plan_label} · membre`);
      else setPlanBadge(null);
    }).catch(() => {});
  }, [user?.id]);
  const fileRef = useRef(null);

  if (user === null) return null;

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.uploadAvatar(fd);
      await refreshUser();
      toast.success("Photo mise à jour");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Échec de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async (updates) => {
    await api.updateProfile(updates);
    await refreshUser();
    setShowProfile(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Logged-in but e-mail not confirmed yet → block access and ask for the code.
  if (user && user.email_verified === false) {
    const submitCode = async (e) => {
      e.preventDefault();
      setVerifying(true);
      try {
        await api.verifyEmail(code.trim());
        await refreshUser();
        toast.success("E-mail confirmé, bonne lecture ! 📰");
        navigate("/");
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Code incorrect");
      } finally {
        setVerifying(false);
      }
    };
    const resend = async () => {
      setResending(true);
      try {
        await api.resendVerification();
        toast.success("Un nouveau code vient d'être envoyé par e-mail.");
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Patientez avant de renvoyer");
      } finally {
        setResending(false);
      }
    };
    return (
      <div className="max-w-md mx-auto px-4 pt-10" data-testid="verify-email-view">
        <div className="flex items-center justify-center mb-6"><Wordmark imgClass="h-8 w-auto" /></div>
        <form onSubmit={submitCode} className="bg-card border border-border rounded-2xl p-6 space-y-4 text-center">
          <Mail className="w-12 h-12 text-primary mx-auto" />
          <h1 className="font-serif font-black text-2xl">Confirmez votre e-mail</h1>
          <p className="text-sm text-muted-foreground">
            Nous avons envoyé un code à 6 chiffres à <strong>{user.email}</strong>. Saisissez-le pour accéder à vos revues. Pensez à regarder vos spams.
          </p>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="______"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="text-center text-2xl tracking-[0.5em] font-mono h-14"
            data-testid="verify-code-input"
          />
          <Button type="submit" className="w-full gap-2" disabled={verifying || code.length !== 6} data-testid="verify-code-submit">
            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Vérifier
          </Button>
          <button type="button" onClick={resend} disabled={resending} className="text-sm text-primary hover:underline disabled:opacity-50" data-testid="verify-resend">
            {resending ? "Envoi…" : "Renvoyer le code"}
          </button>
          <button type="button" onClick={() => { logout(); toast.success("Déconnecté"); }} className="block w-full text-xs text-muted-foreground hover:text-foreground mt-2" data-testid="verify-logout">
            Utiliser un autre compte
          </button>
        </form>
      </div>
    );
  }

  if (user) {
    return (
      <div className="max-w-lg mx-auto px-4 pt-8 pb-10">
        <div className="bg-card border border-border rounded-2xl p-6 text-center">
          <div className="relative w-24 h-24 mx-auto mb-4">
            {user.avatar_url ? (
              <img src={avatarUrl(user.id, user.last_activity_at || "")} alt={user.name}
                className="w-24 h-24 rounded-full object-cover border-2 border-primary/20" data-testid="account-avatar" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center font-serif font-black text-4xl">
                {((user.first_name || "").trim()[0] || (user.name || "").trim().split(/\s+/)[0]?.[0] || user.email[0]).toUpperCase()}{((user.last_name || "").trim()[0] || (user.name || "").trim().split(/\s+/)[1]?.[0] || "").toUpperCase()}
              </div>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
              data-testid="account-avatar-upload" aria-label="Changer la photo">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} data-testid="account-avatar-input" />
          </div>
          <h1 className="font-serif font-black text-2xl" data-testid="account-name">{user.first_name || (user.name || user.email).split(" ")[0]}</h1>
          <AccountInfo user={user} />
          <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
            {planBadge && (
              <span className="text-xs font-mono bg-primary/10 text-primary rounded-full px-3 py-1" data-testid="account-plan-badge">{planBadge}</span>
            )}
            {AMBASSADOR_VISIBLE && user.ambassador && (
              <span className="text-xs font-mono bg-amber-100 text-amber-800 rounded-full px-3 py-1" data-testid="account-ambassador-badge">Ambassadeur</span>
            )}
            {user.role === "admin" && (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono bg-secondary rounded-full px-3 py-1"><Shield className="w-3 h-3 text-primary" /> Administrateur</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <Link to="/favoris"><Button variant="outline" className="w-full gap-2"><Heart className="w-4 h-4" /> Favoris</Button></Link>
          <Link to="/mes-revues"><Button variant="outline" className="w-full gap-2"><BookOpen className="w-4 h-4" /> Mes revues</Button></Link>
        </div>

        {monetOn && !user.ambassador && user.role !== "admin" && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Link to="/compte/abonnement"><Button variant="outline" className="w-full gap-2" data-testid="account-link-abonnement"><Crown className="w-4 h-4 text-amber-500" /> Mon abonnement</Button></Link>
            <Link to="/compte/points"><Button variant="outline" className="w-full gap-2" data-testid="account-link-points"><Coins className="w-4 h-4 text-amber-500" /> Points Capital</Button></Link>
          </div>
        )}

        <div className="mt-4 bg-card border border-border rounded-2xl p-5">
          <button onClick={() => setShowProfile((s) => !s)} className="flex items-center justify-between w-full" data-testid="toggle-profile-form">
            <span className="font-serif font-bold text-lg flex items-center gap-2"><User className="w-5 h-5 text-primary" /> Mes coordonnées</span>
            <span className="text-sm text-primary">{showProfile ? "Masquer" : "Compléter"}</span>
          </button>
          {showProfile && (
            <div className="mt-4">
              <ProfileForm initial={user} onSave={saveProfile} prefix="profile" />
            </div>
          )}
        </div>

        {user.role === "admin" && (
          <Link to="/admin"><Button className="w-full gap-2 mt-3 bg-[#0A2472] hover:bg-[#0a2472]/90"><Shield className="w-4 h-4" /> Administration</Button></Link>
        )}
        <Button variant="ghost" className="w-full gap-2 mt-3 text-destructive" data-testid="account-logout"
          onClick={() => { logout(); toast.success("Déconnecté"); navigate("/"); }}>
          <LogOut className="w-4 h-4" /> Déconnexion
        </Button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        const u = await login(email, password);
        toast.success("Bienvenue !");
        if (u && u.email_verified === false) return; // show the verify screen
      }
      else {
        if (!firstName.trim()) { toast.error("Le prénom est obligatoire"); setLoading(false); return; }
        if (!lastName.trim()) { toast.error("Le nom est obligatoire"); setLoading(false); return; }
        if (!loc.postal.trim()) { toast.error("Le code postal est obligatoire"); setLoading(false); return; }
        if (!loc.city.trim()) { toast.error("Veuillez renseigner votre ville"); setLoading(false); return; }
        const u = await register(email, password, {
          first_name: firstName, last_name: lastName,
          postal_code: loc.postal.trim(), city: loc.city.trim(), country: loc.country,
        });
        toast.success("Compte créé ! Confirmez votre e-mail pour commencer.");
        if (u && u.email_verified === false) return; // show the verify screen
      }
      navigate("/");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Déjà inscrit (prospect ou client) : on l'oriente vers la connexion,
        // e-mail pré-rempli, pour retrouver ses informations sans les ressaisir.
        setTab("login");
        setPassword("");
        toast.message("Vous avez déjà un compte", {
          description: "Connectez-vous pour retrouver vos informations.",
        });
        setLoading(false);
        return;
      }
      toast.error(formatApiError(err?.response?.data?.detail) || "Échec");
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Échec de l'envoi");
    } finally { setLoading(false); }
  };

  if (forgot) {
    return (
      <div className="max-w-md mx-auto px-4 pt-8" data-testid="forgot-password-view">
        <div className="flex items-center justify-center mb-6">
          <Wordmark imgClass="h-8 w-auto" />
        </div>
        {forgotSent ? (
          <div className="bg-card border border-border rounded-2xl p-6 text-center space-y-3">
            <Mail className="w-12 h-12 text-primary mx-auto" />
            <h1 className="font-serif font-black text-xl">Vérifiez vos emails</h1>
            <p className="text-sm text-muted-foreground">Si un compte existe pour <strong>{email}</strong>, un lien de réinitialisation vient d'être envoyé. Pensez à regarder vos spams.</p>
            <Button className="w-full" onClick={() => { setForgot(false); setForgotSent(false); }} data-testid="forgot-back-login">Retour à la connexion</Button>
          </div>
        ) : (
          <form onSubmit={submitForgot} className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <div className="text-center">
              <h1 className="font-serif font-black text-2xl">Mot de passe oublié</h1>
              <p className="text-sm text-muted-foreground mt-1">Entrez votre email : nous vous enverrons un lien pour créer un nouveau mot de passe.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())}
                placeholder="vous@exemple.com" required data-testid="forgot-input-email"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" />
            </div>
            <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Envoyer le lien
            </Button>
            <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-cancel">
              Annuler
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 pt-8">
      <div className="flex flex-col items-center justify-center mb-6 text-center gap-2">
        <Wordmark imgClass="h-8 w-auto" />
        <h2 className="font-serif font-black text-2xl mt-2 leading-tight" data-testid="auth-greeting">
          Installez-vous : votre espace vous attend.
        </h2>
        <p className="text-sm text-muted-foreground">
          {tab === "login" ? "Content de vous revoir." : "Créez votre espace en quelques instants."}
        </p>
      </div>

      <div className="flex bg-secondary rounded-xl p-1 mb-5">
        <button onClick={() => setTab("login")} data-testid="tab-login"
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "login" ? "bg-card shadow" : "text-muted-foreground"}`}>Mon espace</button>
        <button onClick={() => setTab("register")} data-testid="tab-register"
          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "register" ? "bg-card shadow" : "text-muted-foreground"}`}>Créer mon espace</button>
      </div>

      <form onSubmit={submit} className="space-y-4 bg-card border border-border rounded-2xl p-6">
        {tab === "register" && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="first-name">Prénom</Label>
              <Input id="first-name" value={firstName} onChange={(e) => setFirstName(capitalizeFirst(e.target.value))} placeholder="Votre prénom" required data-testid="input-first-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name">Nom</Label>
              <Input id="last-name" value={lastName} onChange={(e) => setLastName(capitalizeFirst(e.target.value))} placeholder="Votre nom" required data-testid="input-last-name" />
            </div>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} placeholder="vous@exemple.com" required data-testid="input-email" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <div className="relative">
            <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="input-password" className="pr-10" />
            <button type="button" onClick={() => setShowPassword((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              data-testid="toggle-password-visibility" aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {tab === "register" && (
          <PostalCity value={loc} onChange={setLoc} idPrefix="reg" />
        )}
        {tab === "login" && (
          <button type="button" onClick={() => { setForgotSent(false); setForgot(true); }}
            className="block text-left text-sm text-primary hover:underline -mt-1" data-testid="forgot-password-link">
            Mot de passe oublié ?
          </button>
        )}
        <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="submit-auth">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />}
          {tab === "login" ? "Mon espace" : "Créer mon espace"}
        </Button>
      </form>
    </div>
  );
}
